import {
  BookOpen,
  CalendarClock,
  Folder,
  Library,
  SlidersHorizontal,
  Wrench,
} from "lucide-react";
import type { MySelf } from "@/utils";
import { AUDIOGAME_LIST_TITLE } from "../books/page-title";

/** Any icon component, matching the shape `ds/IconAction` accepts. */
type IconComponent = React.ComponentType<{
  className?: string;
  "aria-hidden"?: React.AriaAttributes["aria-hidden"];
}>;

// TODO: point at the real handbook. Hardcoded like the app's other external URLs;
// there is no frontend config module for them.
const DOCUMENTATION_URL =
  "https://playnook.notion.site/Creator-Program-Guidelines-3b88a28ca7ad8070b36fc8ad33e55de6";

export interface NavItem {
  label: string;
  icon: IconComponent;
  /** An in-app route. Exactly one of `to` / `href` is set. */
  to?: string;
  /** A Django page or an external site — a plain anchor, full page load. */
  href?: string;
  /** Marks `href` as leaving the app, so the link says so to a screen reader. */
  external?: boolean;
  /** Hidden unless this returns true. Omitted means always visible. */
  visible?: (user: MySelf) => boolean;
}

export interface NavSection {
  id: string;
  items: NavItem[];
}

/**
 * The single source of truth for the global navigation — data, not JSX, so the
 * rail and the drawer cannot drift.
 *
 * Keep it query-free: the test API mock throws on an unmatched URL, so one badge
 * count here breaks every route sweep at once.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: "main",
    items: [
      // From the page's own constant: usePageTitle announces it on arrival, so a
      // different name here would be heard instead of the one activated.
      { label: AUDIOGAME_LIST_TITLE, icon: Library, to: "/" },
      {
        label: "Publishing calendar",
        icon: CalendarClock,
        to: "/calendar",
      },
      { label: "Tasks", icon: Wrench, to: "/tasks" },
      {
        label: "Documentation",
        icon: BookOpen,
        href: DOCUMENTATION_URL,
        external: true,
      },
    ],
  },
  {
    id: "admin",
    items: [
      {
        label: "Admin panel",
        icon: SlidersHorizontal,
        href: "/admin",
        visible: (user) => user.isStaff,
      },
      {
        label: "File browser",
        icon: Folder,
        href: "https://filebrowser.playnook.games",
        external: true,
        visible: (user) => user.isSuperuser,
      },
    ],
  },
];

/** Drops the items this user may not see, then any section left empty. */
export function visibleSections(user: MySelf): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.visible?.(user) ?? true),
  })).filter((section) => section.items.length > 0);
}
